import { useEffect, useMemo, useState } from "react";

import {
  confirmInstitutionProductChanges,
  listInstitutionProducts,
  previewInstitutionProductChanges,
  type Institution,
  type InstitutionProduct,
  type InstitutionProductChange,
  type InstitutionProductPage,
  type InstitutionProductPreview,
  type InstitutionProductStatus,
} from "./api";

type Props = {
  institution: Institution;
  initialPage?: InstitutionProductPage;
  onClose: () => void;
};

type Draft = Record<string, {
  status: InstitutionProductStatus;
  institutionPrice: string;
  agentCommission: string;
  costs: Record<string, string>;
  manualSupplierCost: string;
  manualSupplierBillingMode: string;
}>;

type Notice = {
  title: string;
  message: string;
};

const STATUS_LABELS: Record<InstitutionProductStatus, string> = {
  OPEN: "已开通",
  VISIBLE_DISABLED: "展示未开通",
  HIDDEN: "隐藏",
};

function makeDraft(items: InstitutionProduct[]): Draft {
  return Object.fromEntries(items.map((product) => [product.product_code, {
    status: product.status,
    institutionPrice: product.institution_price || product.suggested_price || "",
    agentCommission: product.agent_commission ?? "",
    costs: Object.fromEntries(product.suppliers
      .filter((supplier) => supplier.interface_code)
      .map((supplier) => [supplier.interface_code as string, supplier.global_cost ?? ""])),
    manualSupplierCost: product.delivery_mode === "MANUAL"
      ? product.suppliers[0]?.global_cost ?? ""
      : "",
    manualSupplierBillingMode: product.delivery_mode === "MANUAL"
      ? product.suppliers[0]?.supplier_billing_mode ?? ""
      : "",
  }]));
}

function indexProducts(items: InstitutionProduct[]): Record<string, InstitutionProduct> {
  return Object.fromEntries(items.map((product) => [product.product_code, product]));
}

function mergeDraft(draft: Draft, items: InstitutionProduct[]): Draft {
  const missing = items.filter((product) => !draft[product.product_code]);
  return missing.length ? { ...draft, ...makeDraft(missing) } : draft;
}

function applyDraft(items: InstitutionProduct[], draft: Draft): Record<string, InstitutionProduct> {
  return Object.fromEntries(items.map((product) => {
    const value = draft[product.product_code];
    if (!value) return [product.product_code, product];
    return [product.product_code, {
      ...product,
      status: value.status,
      institution_price: value.institutionPrice || null,
      agent_commission: value.agentCommission || null,
      suppliers: product.suppliers.map((supplier) => {
        if (product.delivery_mode === "MANUAL") {
          return {
            ...supplier,
            global_cost: value.manualSupplierCost || null,
            supplier_billing_mode: value.manualSupplierBillingMode || null,
          };
        }
        if (supplier.interface_code && supplier.cost_editable) {
          return { ...supplier, global_cost: value.costs[supplier.interface_code] || null };
        }
        return supplier;
      }),
    }];
  }));
}

function buildChanges(
  items: InstitutionProduct[],
  draft: Draft,
  hasAgent: boolean,
): InstitutionProductChange[] {
  return items.flatMap((product) => {
    const current = draft[product.product_code];
    if (!current) return [];
    const interfaceCosts = Object.fromEntries(product.suppliers
      .filter((supplier) => supplier.interface_code && supplier.cost_editable)
      .map((supplier) => [supplier.interface_code as string, current.costs[supplier.interface_code as string] || null]));
    const originalCosts = Object.fromEntries(product.suppliers
      .filter((supplier) => supplier.interface_code && supplier.cost_editable)
      .map((supplier) => [supplier.interface_code as string, supplier.global_cost]));
    const manualSupplier = product.delivery_mode === "MANUAL" ? product.suppliers[0] : undefined;
    const manualSupplierChanged = !!manualSupplier && (
      (current.manualSupplierCost || null) !== manualSupplier.global_cost
      || (current.manualSupplierBillingMode || null) !== manualSupplier.supplier_billing_mode
    );
    const originalInstitutionPrice = product.institution_price || product.suggested_price || null;
    const agentCommission = hasAgent ? current.agentCommission || null : null;
    const originalAgentCommission = hasAgent ? product.agent_commission : null;
    const changed = current.status !== product.status
      || (current.institutionPrice || null) !== originalInstitutionPrice
      || agentCommission !== originalAgentCommission
      || JSON.stringify(interfaceCosts) !== JSON.stringify(originalCosts)
      || manualSupplierChanged;
    return changed ? [{
      product_code: product.product_code,
      status: current.status,
      institution_price: current.institutionPrice || null,
      agent_commission: agentCommission,
      interface_costs: interfaceCosts,
      ...(manualSupplier ? {
        manual_supplier_cost: current.manualSupplierCost || null,
        manual_supplier_billing_mode: current.manualSupplierBillingMode || null,
      } : {}),
    }] : [];
  });
}

export function InstitutionProducts({ institution, initialPage, onClose }: Props) {
  const [result, setResult] = useState<InstitutionProductPage | null>(initialPage ?? null);
  const [draft, setDraft] = useState<Draft>(() => makeDraft(initialPage?.items ?? []));
  const [loadedProducts, setLoadedProducts] = useState<Record<string, InstitutionProduct>>(
    () => indexProducts(initialPage?.items ?? []),
  );
  const [query, setQuery] = useState("");
  const [moduleCode, setModuleCode] = useState("");
  const [status, setStatus] = useState<InstitutionProductStatus | "">("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(!initialPage);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState<InstitutionProductPreview | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);

  async function load(resetDraft = false) {
    setLoading(true);
    setError("");
    try {
      const next = await listInstitutionProducts(institution.id, {
        query, moduleCode, status, page,
      });
      setResult(next);
      setLoadedProducts((current) => resetDraft ? indexProducts(next.items) : { ...current, ...indexProducts(next.items) });
      setDraft((current) => resetDraft ? makeDraft(next.items) : mergeDraft(current, next.items));
    } catch (reason) {
      const message = reason instanceof Error ? reason.message : "产品配置加载失败";
      setError(message);
      setNotice({ title: "加载产品配置失败", message });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (initialPage) return;
    void load();
  }, [institution.id, moduleCode, status, page]);

  const items = result?.items ?? [];
  const changes = useMemo(
    () => buildChanges(Object.values(loadedProducts), draft, Boolean(institution.agent)),
    [loadedProducts, draft, institution.agent],
  );
  const modules = [...new Map(items.map((item) => [item.service_module_code, item.service_module_name])).entries()];
  const totalPages = Math.max(1, Math.ceil((result?.total ?? 0) / 5));

  function updateProduct(product: InstitutionProduct, patch: Partial<Draft[string]>) {
    const current = draft[product.product_code];
    if (!current) return;
    const next = { ...current, ...patch };
    if (patch.status === "OPEN" && !next.institutionPrice) {
      next.institutionPrice = product.suggested_price ?? "";
    }
    setDraft({ ...draft, [product.product_code]: next });
  }

  function updateGlobalCost(interfaceCode: string, cost: string) {
    setDraft(Object.fromEntries(Object.entries(draft).map(([productCode, value]) => [
      productCode,
      Object.prototype.hasOwnProperty.call(value.costs, interfaceCode)
        ? { ...value, costs: { ...value.costs, [interfaceCode]: cost } }
        : value,
    ])));
  }

  function updateManualSupplier(productCode: string, patch: Partial<Pick<Draft[string], "manualSupplierCost" | "manualSupplierBillingMode">>) {
    const current = draft[productCode];
    if (!current) return;
    setDraft({ ...draft, [productCode]: { ...current, ...patch } });
  }

  async function requestPreview() {
    if (!changes.length) {
      setNotice({ title: "没有可保存的修改", message: "尚未修改产品状态、对客报价、代理佣金或供应商参数。" });
      return;
    }
    setSaving(true);
    setError("");
    try {
      setPreview(await previewInstitutionProductChanges(institution.id, changes));
    } catch (reason) {
      const message = reason instanceof Error ? reason.message : "修改内容校验失败";
      setError(message);
      setNotice({ title: "无法确认修改", message });
    } finally {
      setSaving(false);
    }
  }

  async function confirm() {
    if (!preview) return;
    setSaving(true);
    setError("");
    try {
      await confirmInstitutionProductChanges(institution.id, changes, preview.preview_token);
      setSaved(true);
      setPreview(null);
      if (initialPage) {
        const committed = applyDraft(Object.values(loadedProducts), draft);
        const committedItems = initialPage.items.map((product) => committed[product.product_code] ?? product);
        setLoadedProducts(committed);
        setDraft(makeDraft(Object.values(committed)));
        setResult({ ...initialPage, items: committedItems });
      } else {
        await load(true);
      }
    } catch (reason) {
      const message = reason instanceof Error ? reason.message : "产品配置保存失败";
      setError(message);
      setNotice({ title: "保存未成功", message });
    } finally {
      setSaving(false);
    }
  }

  return <div className="platform-modal-layer" role="presentation">
    <section className="platform-modal platform-product-modal" role="dialog" aria-modal="true" aria-labelledby="institution-products-title">
      <header className="platform-modal-heading platform-product-heading">
        <div><h2 id="institution-products-title">产品开通与价格配置</h2><p>{institution.name} <span>{institution.organization_type === "LAW_FIRM" ? "律所" : "机构"}</span></p></div>
        <button aria-label="关闭" type="button" onClick={onClose}>×</button>
      </header>

      <form className="platform-product-toolbar" onSubmit={(event) => { event.preventDefault(); setPage(1); if (!initialPage) void load(); }}>
        <input aria-label="搜索产品、供应商或接口" placeholder="搜索产品、供应商或接口" value={query} onChange={(event) => setQuery(event.target.value)} />
        <select aria-label="服务模块" value={moduleCode} onChange={(event) => { setModuleCode(event.target.value); setPage(1); }}><option value="">全部服务模块</option>{modules.map(([code, name]) => <option key={code} value={code}>{name}</option>)}</select>
        <select aria-label="产品状态" value={status} onChange={(event) => { setStatus(event.target.value as InstitutionProductStatus | ""); setPage(1); }}><option value="">全部状态</option>{Object.entries(STATUS_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select>
        <button type="submit">查询</button>
        <span>共 {result?.total ?? 0} 个产品</span>
      </form>

      <div className="platform-product-table-wrap">
        <table className="platform-product-table">
          <colgroup>
            <col style={{ width: "8%" }} /><col style={{ width: "10%" }} />
            <col style={{ width: "11%" }} /><col style={{ width: "21%" }} />
            <col style={{ width: "8%" }} /><col style={{ width: "7%" }} />
            <col style={{ width: "7%" }} /><col style={{ width: "6%" }} />
            <col style={{ width: "8%" }} /><col style={{ width: "8%" }} />
            <col style={{ width: "6%" }} />
          </colgroup>
          <thead>
            <tr><th className="is-product" colSpan={2}>产品</th><th className="is-supplier" colSpan={4}>供应商</th><th className="is-customer" colSpan={5}>对客</th></tr>
            <tr><th className="is-product">服务模块</th><th className="is-product">细分产品</th><th className="is-supplier">供应商</th><th className="is-supplier">接口</th><th className="is-supplier">通用成本价</th><th className="is-supplier">计费方式</th><th className="is-customer">计费方式</th><th className="is-customer">建议报价</th><th className="is-customer">对客报价</th><th className="is-customer is-agent-commission">代理佣金</th><th className="is-customer">产品状态</th></tr>
          </thead>
          <tbody>
            {items.flatMap((product) => product.suppliers.map((supplier, supplierIndex) => {
              const value = draft[product.product_code];
              const interfaceCode = supplier.interface_code;
              const manualSupplier = product.delivery_mode === "MANUAL";
              const canEditCost = supplier.cost_editable && (interfaceCode || manualSupplier);
              return <tr key={`${product.product_code}-${interfaceCode ?? "manual"}`}>
                {supplierIndex === 0 && <td rowSpan={product.suppliers.length}>{product.service_module_name}</td>}
                {supplierIndex === 0 && <td rowSpan={product.suppliers.length}><strong>{product.product_name}</strong></td>}
                <td><strong>{supplier.supplier_name || "—"}</strong></td>
                <td>{product.delivery_mode === "MANUAL" ? <><strong>人工查询</strong><small>无供应商接口</small></> : <><strong>{supplier.interface_name || "—"}</strong><small>{supplier.supplier_product_code || `内部代码：${interfaceCode || "—"}`}</small><small>{supplier.document_version ? `文档版本：${supplier.document_version}` : "供应商原名及版本待核对"}</small></>}</td>
                <td>{canEditCost ? <label className="platform-money-input"><span>¥</span><input aria-label={`${manualSupplier ? "人工查询" : supplier.interface_name}通用成本价`} inputMode="decimal" value={manualSupplier ? value?.manualSupplierCost ?? "" : value?.costs[interfaceCode as string] ?? ""} onChange={(event) => manualSupplier ? updateManualSupplier(product.product_code, { manualSupplierCost: event.target.value }) : updateGlobalCost(interfaceCode as string, event.target.value)} /></label> : "—"}</td>
                <td>{manualSupplier ? <input className="platform-supplier-billing-input" aria-label="人工查询供应商计费方式" maxLength={32} placeholder="例如：按单收费" value={value?.manualSupplierBillingMode ?? ""} onChange={(event) => updateManualSupplier(product.product_code, { manualSupplierBillingMode: event.target.value })} /> : supplier.supplier_billing_mode || "—"}</td>
                {supplierIndex === 0 && <td rowSpan={product.suppliers.length}>{product.customer_billing_mode || "—"}</td>}
                {supplierIndex === 0 && <td rowSpan={product.suppliers.length}>{product.suggested_price ? `¥${product.suggested_price}` : "—"}</td>}
                {supplierIndex === 0 && <td rowSpan={product.suppliers.length}><label className="platform-money-input"><span>¥</span><input aria-label={`${product.product_name}对客报价`} inputMode="decimal" value={value?.institutionPrice ?? ""} onChange={(event) => updateProduct(product, { institutionPrice: event.target.value })} /></label></td>}
                {supplierIndex === 0 && <td rowSpan={product.suppliers.length}>{institution.agent ? <label className="platform-money-input platform-agent-commission-input"><span>¥</span><input aria-label={`${product.product_name}代理佣金`} inputMode="decimal" value={value?.agentCommission ?? ""} onChange={(event) => updateProduct(product, { agentCommission: event.target.value })} /><small>代理每笔佣金</small></label> : "—"}</td>}
                {supplierIndex === 0 && <td rowSpan={product.suppliers.length}><select aria-label={`${product.product_name}产品状态`} value={value?.status ?? product.status} onChange={(event) => updateProduct(product, { status: event.target.value as InstitutionProductStatus })}>{Object.entries(STATUS_LABELS).map(([statusValue, label]) => <option key={statusValue} value={statusValue}>{label}</option>)}</select></td>}
              </tr>;
            }))}
            {loading && <tr><td colSpan={11} className="platform-product-empty">正在加载产品…</td></tr>}
            {!loading && !error && items.length === 0 && <tr><td colSpan={11} className="platform-product-empty">暂无可配置产品</td></tr>}
          </tbody>
        </table>
      </div>
      <footer className="platform-product-footer"><div><span>每页 5 个产品</span><button type="button" disabled={page <= 1} onClick={() => setPage(page - 1)}>上一页</button><b>{page} / {totalPages}</b><button type="button" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>下一页</button></div><div><button className="is-secondary" type="button" onClick={onClose}>取消</button><button className="platform-primary-button" type="button" disabled={saving} onClick={() => void requestPreview()}>保存并确认修改</button></div></footer>

      {preview && <div className="platform-product-confirm-layer" role="presentation"><section className="platform-modal platform-product-confirm" role="alertdialog" aria-modal="true" aria-labelledby="product-confirm-title"><h2 id="product-confirm-title">确认产品配置修改</h2><p>本次修改将在确认后一次性生效。</p><dl><div><dt>新开通产品</dt><dd>{preview.diff.opened.length} 项</dd></div><div><dt>展示或隐藏调整</dt><dd>{preview.diff.closed_or_hidden.length} 项</dd></div><div><dt>对客报价调整</dt><dd>{preview.diff.price_changes.length} 项</dd></div><div className="is-agent"><dt>代理佣金调整</dt><dd>{preview.diff.agent_commission_changes.length} 项</dd></div><div className="is-global"><dt>全局供应商成本调整</dt><dd>{preview.diff.global_cost_changes.length} 项（影响全部机构）</dd></div><div><dt>人工供应商商业参数</dt><dd>{preview.diff.manual_supplier_changes.length} 项</dd></div></dl>{error && <p role="alert" className="platform-modal-error">保存未成功：{error}</p>}<div className="platform-modal-actions"><button className="is-secondary" type="button" disabled={saving} onClick={() => setPreview(null)}>返回修改</button><button className="platform-primary-button" type="button" disabled={saving} onClick={() => void confirm()}>{saving ? "正在保存…" : "确认并保存"}</button></div></section></div>}
      {saved && <div className="platform-product-confirm-layer" role="presentation"><section className="platform-modal platform-product-confirm" role="alertdialog" aria-modal="true" aria-labelledby="product-saved-title"><h2 id="product-saved-title">保存成功</h2><p>该机构的产品权限及价格配置已保存。</p>{error && <p role="alert" className="platform-modal-error">保存已成功，但列表刷新失败，请重新打开查看。</p>}<div className="platform-modal-actions"><button autoFocus className="platform-primary-button" type="button" disabled={saving} onClick={() => setSaved(false)}>{saving ? "正在刷新…" : "知道了"}</button></div></section></div>}
      {notice && <div className="platform-product-confirm-layer" role="presentation"><section className="platform-modal platform-product-confirm" role="alertdialog" aria-modal="true" aria-labelledby="product-notice-title"><h2 id="product-notice-title">{notice.title}</h2><p>{notice.message}</p><div className="platform-modal-actions"><button autoFocus className="platform-primary-button" type="button" onClick={() => setNotice(null)}>知道了</button></div></section></div>}
    </section>
  </div>;
}
